import React, { useState, useEffect } from 'react';
import { Copy, Chrome, Check, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from './ui/use-toast';
import { authFetch } from '../utils/auth-fetch';

interface ChromeExtensionActivationProps {
  siteId: string;
  siteName: string;
}

export default function ChromeExtensionActivation({ siteId, siteName }: ChromeExtensionActivationProps) {
  const [authCode, setAuthCode] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (siteId) {
      fetchAuthCode();
    }
  }, [siteId]);

  const fetchAuthCode = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`/api/extension-auth/generate/${siteId}`);
      if (!response.ok) {
        throw new Error('Failed to generate auth code');
      }
      const data = await response.json();
      setAuthCode(data.authCode);
    } catch (error) {
      console.error('Error fetching auth code:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate Chrome Extension code',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(authCode);
      setCopied(true);
      toast({
        title: 'Copied!',
        description: 'Three-word code copied to clipboard',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: 'Copy failed',
        description: 'Please select and copy the code manually',
        variant: 'destructive',
      });
    }
  };

  const regenerateCode = async () => {
    await fetchAuthCode();
    toast({
      title: 'Code regenerated',
      description: 'A new three-word code has been generated',
    });
  };

  if (!authCode && !loading) {
    return null;
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Chrome className="h-5 w-5" />
          Chrome Extension Activation
        </CardTitle>
        <CardDescription>
          Share this three-word code with users to activate the PageMind Chrome Extension on {siteName}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-500" />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <div className="relative">
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-purple-700 dark:text-purple-300 tracking-wide">
                      {authCode}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyToClipboard}
                  className="min-w-[100px]"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={regenerateCode}
                  className="min-w-[100px]"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Refresh
                </Button>
              </div>
            </div>

            <Alert>
              <AlertDescription>
                <strong>How to use:</strong>
                <ol className="mt-2 ml-4 list-decimal space-y-1 text-sm">
                  <li>Install the PageMind Chrome Extension</li>
                  <li>Visit {siteName}</li>
                  <li>Click the extension icon and enter this three-word code</li>
                  <li>PageMind analytics will activate automatically</li>
                </ol>
              </AlertDescription>
            </Alert>

            <div className="flex gap-2">
              <Button
                variant="default"
                size="sm"
                className="flex-1"
                onClick={() => window.open('https://chrome.google.com/webstore/detail/pagemind', '_blank')}
              >
                <Chrome className="h-4 w-4 mr-2" />
                Install Extension
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => window.open('/docs/chrome-extension', '_blank')}
              >
                View Documentation
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}